import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { downloadBlob, errMsg } from "@/lib/applyApi";

// Renders every page (server-rendered from the exact stored PDF) so all pages scroll on every device.
export function PdfViewer({ open, onClose, loadPages, loadPdf }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setDoc(null); return; }
    loadPages().then(setDoc).catch((e) => { toast.error(errMsg(e, "Could not open the agreement")); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setBusy(true);
    try { downloadBlob(await loadPdf(), doc.name); } catch (e) { toast.error(errMsg(e, "Download failed")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pdf-viewer" className="w-[calc(100%-1rem)] max-w-3xl h-[92vh] p-0 flex flex-col gap-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b border-stone-200 text-left">
          <DialogTitle data-testid="pdf-viewer-title" className="text-sm sm:text-base pr-8 flex items-start gap-2"><FileText className="h-4 w-4 mt-0.5 shrink-0 text-amber-800" />{doc?.title || "Loading agreement…"}</DialogTitle>
          <DialogDescription className="flex items-center justify-between gap-2">
            <span data-testid="pdf-viewer-pages">{doc ? `${doc.page_count} pages — scroll to read every page` : ""}</span>
            <Button data-testid="pdf-download" size="sm" onClick={download} disabled={!doc || busy} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" />Download PDF</>}
            </Button>
          </DialogDescription>
        </DialogHeader>
        <div data-testid="pdf-viewer-scroll" className="flex-1 overflow-y-auto bg-stone-200 p-3 space-y-3">
          {!doc && <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-500" /></div>}
          {doc?.pages.map((src, i) => (
            <img key={i} src={src} alt={`Page ${i + 1} of ${doc.page_count}`} data-testid={`pdf-page-${i + 1}`} className="w-full bg-white shadow rounded" />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
